import React, { useState, useEffect } from 'react';
import { Plus, Search, Calendar, User, CreditCard, Receipt, LayoutGrid, List, IndianRupee } from 'lucide-react';
import { Payment, Sale, Customer, Plant } from '../../types';
import { format } from 'date-fns';
import { paymentService, orderService, customerService } from '../../services/firebase';
import ReceiptGenerator from '../receipt/ReceiptGenerator';
import { printReceiptWithLogo } from '../../utils/receiptUtils';

const PaymentTracking: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [orders, setOrders] = useState<Sale[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [outstandingViewMode, setOutstandingViewMode] = useState<'grid' | 'list'>('list');
  const [formData, setFormData] = useState({
    saleId: '',
    customerId: '',
    amount: '',
    paymentMethod: 'cash',
    notes: '',
  });
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState<{
    order: Sale;
    customer: Customer;
    payments: Payment[];
    receiptType: 'order' | 'payment' | 'completion';
    plantDetails?: Plant;
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [customersData, ordersData, paymentsData] = await Promise.all([
          customerService.getAllCustomers(),
          orderService.getAllOrders(),
          paymentService.getAllPayments()
        ]);
        
        setCustomers(customersData);
        setOrders(ordersData);
        setPayments(paymentsData);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        // You might want to show an error message to the user here
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const paymentData = {
        saleId: formData.saleId,
        customerId: formData.customerId,
        amount: parseFloat(formData.amount),
        paymentMethod: formData.paymentMethod,
        notes: formData.notes,
      };

      const newPaymentId = await paymentService.addPayment(paymentData);
      
      const newPayment: Payment = {
        id: newPaymentId,
        ...paymentData,
        paymentDate: new Date() as any,
      };

      setPayments([newPayment, ...payments]);

      // Refresh orders to get updated status
      let updatedOrders: Sale[] = [];
      try {
        updatedOrders = await orderService.getAllOrders();
        setOrders(updatedOrders);
      } catch (error) {
        console.error('Error refreshing orders:', error);
      }

      // Generate receipt for the payment
      const order = updatedOrders.find(o => o.id === formData.saleId) || orders.find(o => o.id === formData.saleId);
      const customer = customers.find(c => c.id === formData.customerId);
      
      if (order && customer) {
        // Get all payments for this order
        const orderPayments = payments.filter(p => p.saleId === formData.saleId);
        const allPayments = [newPayment, ...orderPayments];
        
        // Determine receipt type
        let receiptType: 'payment' | 'completion' = 'payment';
        if (order.remainingAmount <= 0) {
          receiptType = 'completion';
        }
        
        setReceiptData({
          order,
          customer,
          payments: allPayments,
          receiptType
        });
        setShowReceipt(true);
      }

      resetForm();
    } catch (error) {
      console.error('Error recording payment:', error);
      // You might want to show an error message to the user here
    }
  };

  const resetForm = () => {
    setFormData({
      saleId: '',
      customerId: '',
      amount: '',
      paymentMethod: 'cash',
      notes: '',
    });
    setShowForm(false);
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'cash':
        return <span className="text-lg font-bold">₹</span>;
      case 'bank_transfer':
        return <CreditCard className="h-4 w-4" />;
      case 'cheque':
        return <CreditCard className="h-4 w-4" />;
      default:
        return <span className="text-lg font-bold">₹</span>;
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'cash':
        return 'Cash';
      case 'bank_transfer':
        return 'Bank Transfer';
      case 'cheque':
        return 'Cheque';
      default:
        return 'Cash';
    }
  };

  const formatDate = (dateValue: any): Date => {
    if (!dateValue) return new Date();
    
    // If it's a Firestore Timestamp
    if (dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    
    // If it's already a Date object
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    // If it's a number (timestamp)
    if (typeof dateValue === 'number') {
      return new Date(dateValue);
    }
    
    // Try to create a Date from the value
    return new Date(dateValue);
  };

  const filteredPayments = payments.filter(payment => {
    const order = orders.find(o => o.id === payment.saleId);
    const customer = customers.find(c => c.id === payment.customerId);
    return (
      order?.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.amount.toString().includes(searchTerm)
    );
  });

  const outstandingOrders = orders
    .filter(order => order.remainingAmount > 0)
    .sort((a, b) => {
      const dateA = formatDate(a.orderDate);
      const dateB = formatDate(b.orderDate);
      return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Payment Tracking</h1>
            <p className="text-blue-100 text-lg">Manage payments and track outstanding balances</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-6 py-3 border border-white/20 text-sm font-medium rounded-xl shadow-sm text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 transition-all backdrop-blur-sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Record Payment
          </button>
        </div>
      </div>

      {/* Modern Outstanding Orders */}
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center mr-3">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Outstanding Orders</h3>
            </div>
            {/* View Toggle for Outstanding Orders */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setOutstandingViewMode('grid')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  outstandingViewMode === 'grid'
                    ? 'bg-white dark:bg-gray-600 text-orange-600 dark:text-orange-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
                Grid
              </button>
              <button
                onClick={() => setOutstandingViewMode('list')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  outstandingViewMode === 'list'
                    ? 'bg-white dark:bg-gray-600 text-orange-600 dark:text-orange-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <List className="h-4 w-4" />
                List
              </button>
            </div>
          </div>

          {outstandingOrders.length > 0 ? (
            <>
              {/* List View */}
              {outstandingViewMode === 'list' && (
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-orange-50 dark:bg-orange-900/20">
                      <tr>
                        <th style={{width: '22%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Customer</th>
                        <th style={{width: '13%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Order ID</th>
                        <th style={{width: '15%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total Amount</th>
                        <th style={{width: '15%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Paid</th>
                        <th style={{width: '17%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Outstanding</th>
                        <th style={{width: '18%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {outstandingOrders.map((order) => (
                        <tr key={order.id} className="hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors">
                          <td className="px-4 py-4">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                                <span className="text-white text-xs font-bold">
                                  {order.customerName ? order.customerName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'NA'}
                                </span>
                              </div>
                              <span className="font-medium text-gray-900 dark:text-white truncate">
                                {order.customerName}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              #{order.orderId || order.id}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                              ₹{order.totalAmount.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm font-medium text-green-600 dark:text-green-400">
                              ₹{order.amountPaid.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm font-bold text-orange-600 dark:text-orange-400">
                              ₹{order.remainingAmount.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-700">
                              Pending
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Grid View */}
              {outstandingViewMode === 'grid' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {outstandingOrders.map((order) => (
                    <div
                      key={order.id}
                      className="group bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 border border-orange-200 dark:border-orange-700 rounded-xl p-5 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                    >
                      {/* Customer & Order Info */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center shadow-sm">
                            <span className="text-white text-sm font-bold">
                              {order.customerName ? order.customerName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'NA'}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 dark:text-white">{order.customerName}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              Order #{order.orderId || order.id}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-700">
                            Pending
                          </span>
                        </div>
                      </div>

                      {/* Financial Summary */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Amount</p>
                          <p className="font-bold text-gray-900 dark:text-white">₹{order.totalAmount.toLocaleString()}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Amount Paid</p>
                          <p className="font-bold text-green-600 dark:text-green-400">₹{order.amountPaid.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Outstanding Amount Highlight */}
                      <div className="mt-4 p-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg">
                        <div className="flex items-center justify-between text-white">
                          <span className="text-sm font-medium">Outstanding Balance</span>
                          <span className="text-lg font-bold">₹{order.remainingAmount.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CreditCard className="h-8 w-8 text-green-500 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">All Caught Up!</h3>
              <p className="text-gray-500 dark:text-gray-400">No outstanding orders at the moment</p>
            </div>
          )}
        </div>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search payments by farmer name or amount..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        {/* View Toggle */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'grid'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
            Grid
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <List className="h-4 w-4" />
            List
          </button>
        </div>
      </div>

      {/* Payment Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 sm:top-20 mx-auto p-5 border w-11/12 max-w-md sm:max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Record Payment</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Select Order</label>
                  <select
                    required
                    value={formData.saleId}
                    onChange={(e) => {
                      const order = orders.find(o => o.id === e.target.value);
                      setFormData({
                        ...formData,
                        saleId: e.target.value,
                        customerId: order?.customerId || '',
                      });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Choose an order</option>
                    {outstandingOrders.map(order => (
                      <option key={order.id} value={order.id}>
                        {order.customerName} - ₹{order.remainingAmount.toLocaleString()} outstanding
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    maxLength={7}
                    required
                    value={formData.amount}
                    onChange={(e) => {
                      if (e.target.value.length <= 7) {
                        setFormData({ ...formData, amount: e.target.value });
                      }
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                  <select
                    required
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                    placeholder="Optional payment notes..."
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Record Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modern Payments List */}
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-6">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-3">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Recent Payments</h3>
          </div>
          
          {/* List View */}
          {viewMode === 'list' && (
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th style={{width: '22%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Customer</th>
                    <th style={{width: '13%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Order ID</th>
                    <th style={{width: '15%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                    <th style={{width: '15%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Method</th>
                    <th style={{width: '15%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                    <th style={{width: '20%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Notes</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredPayments.map((payment) => {
                    const order = orders.find(o => o.id === payment.saleId);
                    const customer = customers.find(c => c.id === payment.customerId);

                    return (
                      <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                              <span className="text-white text-xs font-bold">
                                {(customer?.name || order?.customerName || 'NA').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white truncate">
                              {customer?.name || order?.customerName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            #{order?.orderId || payment.saleId}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm font-bold text-green-600 dark:text-green-400">
                            ₹{payment.amount.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700">
                            {getPaymentMethodLabel(payment.paymentMethod)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {format(formatDate(payment.paymentDate), 'MMM dd, yyyy')}
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-500 dark:text-gray-400 truncate block">
                            {payment.notes || '-'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredPayments.map((payment) => {
                const order = orders.find(o => o.id === payment.saleId);
                const customer = customers.find(c => c.id === payment.customerId);

                return (
                  <div
                    key={payment.id}
                    className="group bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700 rounded-xl p-5 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                  >
                    {/* Payment Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-sm">
                          {getPaymentMethodIcon(payment.paymentMethod)}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 dark:text-white">
                            {customer?.name || order?.customerName}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            Order #{order?.orderId || payment.saleId}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700">
                          {getPaymentMethodLabel(payment.paymentMethod)}
                        </span>
                      </div>
                    </div>

                    {/* Payment Details */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Payment Amount</p>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                            ₹{payment.amount.toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500 dark:text-gray-400">Payment Date</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {format(formatDate(payment.paymentDate), 'MMM dd, yyyy')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Notes */}
                    {payment.notes && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                        <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">Notes</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{payment.notes}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {filteredPayments.length === 0 && (
        <div className="text-center py-12">
          <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No payments found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search terms.' : 'Get started by recording a payment.'}
          </p>
        </div>
      )}

      {/* Receipt Generator */}
      {showReceipt && receiptData && (
        <ReceiptGenerator
          order={receiptData.order}
          customer={receiptData.customer}
          payments={receiptData.payments}
          plantDetails={receiptData.plantDetails}
          receiptType={receiptData.receiptType}
          onClose={() => {
            setShowReceipt(false);
            setReceiptData(null);
          }}
          onPrint={async () => {
            await printReceiptWithLogo(
              receiptData.order,
              receiptData.customer,
              receiptData.payments,
              receiptData.receiptType,
              receiptData.plantDetails
            );
          }}
        />
      )}
    </div>
  );
};

export default PaymentTracking; 